import "./Tabs.css";

import { ReactNode, TouchEvent, useEffect, useRef, useState } from "react";

export interface TabsItem {
  label: ReactNode;
  render: () => ReactNode;
  /** Optional analytics name fired in onChange */
  analyticsKey?: string;
}

interface TabsProps {
  tabs: TabsItem[];
  initialIndex?: number;
  onChange?: (
    nextIndex: number,
    nextItem: TabsItem,
    prevIndex: number
  ) => void;
  swipeable?: boolean;
}

// Below threshold = treated as a tap so click-on-card still works.
const SWIPE_THRESHOLD_PX = 60;
// Above tolerance = vertical scroll gesture, don't navigate.
const SWIPE_VERTICAL_TOLERANCE_PX = 40;

// Local state — keeps the URL frozen while tabs change.
export function Tabs({ tabs, initialIndex = 0, onChange, swipeable }: TabsProps) {
  const [selectedIndex, setSelectedIndex] = useState(initialIndex);
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const containerRef = useRef<HTMLUListElement>(null);
  const touchStart = useRef<{ x: number; y: number } | null>(null);

  useEffect(() => {
    const activeTab = tabRefs.current[selectedIndex];
    const container = containerRef.current;
    if (activeTab && container) {
      const target =
        activeTab.offsetLeft -
        (container.clientWidth - activeTab.offsetWidth) / 2;
      container.scrollTo({ left: target, behavior: "smooth" });
    }
  }, [selectedIndex]);

  const handleSelect = (nextIndex: number) => {
    if (nextIndex === selectedIndex) return;
    if (nextIndex < 0 || nextIndex >= tabs.length) return;
    onChange?.(nextIndex, tabs[nextIndex], selectedIndex);
    setSelectedIndex(nextIndex);
  };

  const onTouchStart = (e: TouchEvent<HTMLDivElement>) => {
    const t = e.touches[0];
    touchStart.current = { x: t.clientX, y: t.clientY };
  };

  const onTouchEnd = (e: TouchEvent<HTMLDivElement>) => {
    const start = touchStart.current;
    touchStart.current = null;
    if (!start) return;
    const t = e.changedTouches[0];
    const dx = t.clientX - start.x;
    const dy = t.clientY - start.y;
    if (Math.abs(dy) > SWIPE_VERTICAL_TOLERANCE_PX) return;
    if (Math.abs(dx) < SWIPE_THRESHOLD_PX) return;
    handleSelect(dx < 0 ? selectedIndex + 1 : selectedIndex - 1);
  };

  return (
    <div className="tabs-container">
      <ul className="tab-header-container" ref={containerRef}>
        {tabs.map(({ label }, index) => (
          <button
            key={index}
            ref={(el) => (tabRefs.current[index] = el)}
            className={`tab-item ${index === selectedIndex ? "selected" : ""}`}
            onClick={() => handleSelect(index)}
          >
            {label}
          </button>
        ))}
      </ul>
      <div
        className="tab-content"
        onTouchStart={swipeable ? onTouchStart : undefined}
        onTouchEnd={swipeable ? onTouchEnd : undefined}
      >
        {tabs[selectedIndex]?.render()}
      </div>
    </div>
  );
}
